import React, { useContext, useMemo } from 'react';
import type { ClassNameProp, ScaleProp, VoidCallback } from '../common-types';
import { TabContext } from './Tabs';

/**
 * Properties for {@link Tab} component.
 */
export interface TabProps extends ClassNameProp, ScaleProp {
  /**
   * label
   */
  label?: string;
  /**
   * The click handler of the Tab.
   */
  onClick?: VoidCallback;
  /**
   * Not clickable Tab.
   */
  disabled?: boolean;
}

/**
 * Component to manage multiple Tab components as a single group using a
 * `label` value to determine which Tab is `active`.
 *
 * Simple Tab component.
 * This component works as a controlled component within Tabs component.
 *
 * WCAG compliant: `active` Tab is controlled using tab key
 *  and `inactive` Tabs are controlled using arrow key.
 * @return the created {@link JSX.Element}.
 */
export function Tab({
  className = '',
  onClick,
  label,
  scale = 'medium',
  disabled,
  ...props
}: Readonly<TabProps>): JSX.Element {
  const tabContext = useContext(TabContext);
  const key = useMemo(() => label, [label]);

  // add Tab css class depending on scale, active, disabled
  const cName = [
    'ehs-tab',
    `tab-${scale}`,
    's-font',
    className,
    (tabContext?.selectedTab === key && 'active') || '',
    // (active && activated && 'active') || '',
    (disabled && 'disabled') || ''
  ]
    .filter(Boolean)
    .join(' ');

  const onClicked = () => {
    onClick?.();
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    key && tabContext?.handleTabClick(key);
  };

  return (
    <button
      {...props}
      className={cName}
      onClick={onClicked}
      type="button"
      role="tab"
      aria-selected={tabContext?.selectedTab === key}
      data-label={key}
      tabIndex={tabContext?.selectedTab === key ? 0 : -1} // see: https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
      aria-disabled={disabled}
    >
      {label}
    </button>
  );
}
